import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Edit3, Save, X, ImageIcon, MapPin, Calendar } from 'lucide-react';

const EVENT_FIELDS = ['title', 'event_date', 'location', 'description', 'image_url'];

const blankEvent = () => ({
  title: '',
  event_date: '',
  location: '',
  description: '',
  image_url: '',
});

const formatEventDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const EventsManager = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState(blankEvent());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(blankEvent());
  const [loading, setLoading] = useState(false);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data || []);
    } catch {
      toast.error('Échec du chargement des événements');
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/events`, draft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Événement ajouté');
      setDraft(blankEvent());
      fetchEvents();
    } catch {
      toast.error("Échec de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Événement supprimé');
      fetchEvents();
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev.id);
    const next = blankEvent();
    EVENT_FIELDS.forEach((f) => (next[f] = ev[f] || ''));
    setEditDraft(next);
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/events/${editingId}`, editDraft, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Événement mis à jour');
      setEditingId(null);
      fetchEvents();
    } catch {
      toast.error('Échec de la mise à jour');
    }
  };

  return (
    <div className="resource-manager" data-testid="events-manager">
      <Card className="add-video-card">
        <h3>Ajouter un événement</h3>
        <form onSubmit={handleAdd} className="add-video-form">
          <Input
            placeholder="Titre de l'événement"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            data-testid="event-title-input"
          />
          <Input
            type="date"
            placeholder="Date"
            value={draft.event_date}
            onChange={(e) => setDraft({ ...draft, event_date: e.target.value })}
            data-testid="event-date-input"
          />
          <Input
            placeholder="Lieu (ex : Zénith de Paris)"
            value={draft.location}
            onChange={(e) => setDraft({ ...draft, location: e.target.value })}
            data-testid="event-location-input"
          />
          <Input
            placeholder="URL de l'image (https://…)"
            value={draft.image_url}
            onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
            data-testid="event-image-input"
          />
          <textarea
            className="bio-textarea"
            rows={3}
            placeholder="Description de l'événement"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            data-testid="event-description-input"
          />
          <Button type="submit" disabled={loading} data-testid="add-event-button">
            <Plus size={18} />
            Ajouter l'événement
          </Button>
        </form>
      </Card>

      <Card className="videos-list-card">
        <h3>Événements ({events.length})</h3>
        {events.length === 0 ? (
          <div className="empty-state">
            <p>Aucun événement pour l'instant. Ajoutez-en un ci-dessus.</p>
          </div>
        ) : (
          <div className="resource-list">
            {events.map((ev, idx) => (
              <div key={ev.id} className="event-card" data-testid={`event-card-${idx}`}>
                {editingId === ev.id ? (
                  <div className="resource-edit">
                    <Input
                      value={editDraft.title}
                      onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                    />
                    <Input
                      type="date"
                      value={editDraft.event_date}
                      onChange={(e) => setEditDraft({ ...editDraft, event_date: e.target.value })}
                    />
                    <Input
                      value={editDraft.location}
                      onChange={(e) => setEditDraft({ ...editDraft, location: e.target.value })}
                      placeholder="Lieu"
                    />
                    <Input
                      value={editDraft.image_url}
                      onChange={(e) => setEditDraft({ ...editDraft, image_url: e.target.value })}
                      placeholder="URL image"
                    />
                    <textarea
                      className="bio-textarea"
                      rows={3}
                      value={editDraft.description}
                      onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                    />
                    <div className="resource-edit-actions">
                      <Button size="sm" onClick={saveEdit} data-testid={`save-event-${idx}`}>
                        <Save size={14} /> Enregistrer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        <X size={14} /> Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="event-image-wrap">
                      {ev.image_url ? (
                        <img src={ev.image_url} alt={ev.title} className="event-image" />
                      ) : (
                        <div className="event-image placeholder">
                          <ImageIcon size={32} />
                        </div>
                      )}
                    </div>
                    <div className="event-body">
                      <h4>{ev.title}</h4>
                      <div className="event-meta">
                        {ev.event_date && (
                          <span>
                            <Calendar size={14} /> {formatEventDate(ev.event_date)}
                          </span>
                        )}
                        {ev.location && (
                          <span>
                            <MapPin size={14} /> {ev.location}
                          </span>
                        )}
                      </div>
                      {ev.description && <p className="event-description">{ev.description}</p>}
                    </div>
                    <div className="resource-actions">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(ev)} data-testid={`edit-event-${idx}`}>
                        <Edit3 size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(ev.id)}
                        data-testid={`delete-event-${idx}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventsManager;
